'use client';

import React, { useEffect, useRef } from 'react';
import { SessionData } from '@auth0/nextjs-auth0/types';
import Auth0Login from 'components/Auth0Login';
import LearnDropdownButton from 'components/app/header/LearnDropdownButton';
import AvatarButton from 'components/AvatarButton';
import MenuButton from './MenuButton';
import { usePathname } from 'next/navigation';
import XCloseIcon from 'components/icons/XCloseIcon';
import AdminIcon from 'components/icons/AdminIcon';
import UIButton from 'components/buttons/UIButton';
import { NAV_ITEMS } from './navItems';
import { wrapFocus } from 'utils/focus';

export default function MobileNav({
  session,
  isOpen,
  toggleMenu,
  isAdmin,
  isOnAdminPage,
}: {
  session: null | SessionData;
  isOpen: boolean;
  toggleMenu: () => void;
  isAdmin?: boolean;
  isOnAdminPage?: boolean;
}): React.JSX.Element {
  const pathname = usePathname();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  const getFocusableElements = () =>
    dialogRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

  useEffect(() => {
    if (isOpen) {
      previouslyFocusedElement.current = document.activeElement as HTMLElement;
      getFocusableElements()?.[0]?.focus();
    } else {
      previouslyFocusedElement.current?.focus();
    }
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;

    const focusable = getFocusableElements();
    if (focusable) wrapFocus(e, focusable);
  };

  return (
    <>
      {isOpen && (
        <div
          className="mobile-nav-overlay"
          aria-hidden="true"
          onClick={toggleMenu}
        />
      )}
      <div
        ref={dialogRef}
        id="mobile-nav-panel"
        className={`mobile-nav ${isOpen ? 'menuOpen' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-nav-title"
        onKeyDown={handleKeyDown}
      >
        <div className="mobile-nav-button-container top-buttons">
          <h2 id="mobile-nav-title">Menu</h2>
          <UIButton
            buttonType="text"
            className="mobile-nav-close-btn"
            onClick={toggleMenu}
            aria-label="Close menu"
            icon={<XCloseIcon />}
            label=""
          />
        </div>

        <ul className="mobile-nav-button-container main-buttons">
          {NAV_ITEMS.map((item) => (
            <li key={item.slug}>
              {item.type === 'dropdown' ? (
                <LearnDropdownButton
                  toggleMenu={toggleMenu}
                  pathname={pathname}
                />
              ) : (
                <MenuButton
                  toggleMenu={toggleMenu}
                  label={item.label}
                  slug={item.slug}
                  dataTestId={
                    item.dataTestId ? `${item.dataTestId}-main` : undefined
                  }
                  isCurrentPage={item.slug === pathname}
                />
              )}
            </li>
          ))}
        </ul>

        <div className="mobile-nav-auth-container">
          {!session && <Auth0Login session={session} hasCaret={true} />}
          {isAdmin && (
            <UIButton
              buttonType="secondary"
              href="/admin"
              className={`admin-header-link${
                isOnAdminPage ? ' admin-header-link--active' : ''
              }`}
              aria-current={isOnAdminPage ? 'page' : undefined}
              icon={<AdminIcon />}
              iconPosition="left"
              label="Admin"
            />
          )}
          {session && (
            <AvatarButton
              session={session}
              showDropdown={true}
              isMobile={true}
            />
          )}
        </div>
      </div>
    </>
  );
}
